import { useEffect, useState } from 'react';
import { FolderGit2 } from 'lucide-react';
import Modal from '../../../shared/Modal';
import ModalFormActions from '../../../shared/ModalFormActions';
import { TextInput } from '../../../shared/FormControls';

/** Slugify string for path segment: lowercase, replace spaces/special with hyphens. */
function slugify(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Derive code from name: uppercase, spaces to underscores. */
function toCode(value) {
  return value
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
}

/** Form state and payload match backend: name, code, pathSegment, description, sortOrder */
const initialFormState = {
  name: '',
  code: '',
  pathSegment: '',
  description: '',
  sortOrder: 0,
};

const RepositoryModal = ({
  isOpen,
  mode = 'create',
  repository,
  onSubmit,
  onClose,
  isSubmitting = false,
  serverError,
}) => {
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setErrors({});

      if (mode === 'edit' && repository) {
        setFormValues({
          name: repository.name ?? '',
          pathSegment: repository.pathSegment ?? '',
          description: repository.description ?? '',
          sortOrder:
            typeof repository.sortOrder === 'number' ? repository.sortOrder : Number(repository.sortOrder) || 0,
        });
      } else {
        setFormValues(initialFormState);
      }
    }
  }, [isOpen, mode, repository]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.name.trim()) {
      nextErrors.name = 'Repository name is required';
    }
    if (!formValues.pathSegment.trim()) {
      nextErrors.pathSegment = 'Path segment is required';
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((previous) => {
      const next = { ...previous, [field]: value };
      if (field === 'name' && mode === 'create') {

        next.pathSegment = slugify(value);
      }
      return next;
    });
  };

  const handleSortOrderChange = (event) => {
    const value = event.target.value;
    const num = value === '' ? 0 : parseInt(value, 10);
    setFormValues((previous) => ({
      ...previous,
      sortOrder: Number.isNaN(num) ? previous.sortOrder : num,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: formValues.name.trim(),
      pathSegment: formValues.pathSegment.trim().toLowerCase().replace(/\s+/g, '-'),
      description: formValues.description?.trim() ?? '',
      sortOrder: Number(formValues.sortOrder) || 0,
    };

    onSubmit({ mode, payload });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Repository' : 'Create Repository'}
      size="lg"
      icon={<FolderGit2 className="h-6 w-6 text-primary-dark" strokeWidth={1.5} />}
      footer={
        <ModalFormActions
          onCancel={onClose}
          formId="repository-form"
          isSubmitting={isSubmitting}
          submitText={mode === 'edit' ? 'Save changes' : 'Create Repository'}
        />
      }
    >
      <form id="repository-form" className="space-y-4" onSubmit={handleSubmit}>
        {serverError ? (
          <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-1">
          <TextInput
            id="name"
            label="Repository Name"
            type="text"
            value={formValues.name}
            onChange={handleChange('name')}
            placeholder="e.g. New Archive"
            maxLength={200}
            disabled={isSubmitting}
            error={errors.name}
          />

          <TextInput
            id="pathSegment"
            label="Path Segment"
            type="text"
            value={formValues.pathSegment}
            onChange={handleChange('pathSegment')}
            placeholder="e.g. new-archive"
            maxLength={100}
            disabled={isSubmitting}
            error={errors.pathSegment}
          />

          <div className="space-y-1">
            <label htmlFor="description" className="text-sm font-medium text-primary-dark">
              Description
            </label>
            <textarea
              id="description"
              value={formValues.description}
              onChange={handleChange('description')}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-dark/30"
              placeholder="Optional description"
              maxLength={1000}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="sortOrder" className="text-sm font-medium text-primary-dark">
              Sort Order
            </label>
            <input
              id="sortOrder"
              type="number"
              min={0}
              value={formValues.sortOrder}
              onChange={handleSortOrderChange}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-dark/30"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default RepositoryModal;
